"use client";

import {
  Suspense,
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Link from "next/link";

import {
  useRouter,
  useSearchParams,
} from "next/navigation";

import {
  ArrowUpRight,
  Bookmark,
  CheckCircle2,
  Loader2,
  MapPin,
  Search,
  SlidersHorizontal,
  Star,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { apiClient } from "@/lib/api";

type Club = {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  tags: string[];
  campus: string | null;
  logo_url: string | null;
  member_count: number | null;
  star_rating: number | null;
  accepting_members: boolean | null;
  membership_type: string | null;
};

type ClubsResponse = {
  clubs: Club[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
};

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE = 900;

async function fetchClubPage(
  queryString: string,
  page: number,
  signal: AbortSignal
): Promise<ClubsResponse> {
  const params = new URLSearchParams(queryString);

  params.set("page", String(page));
  params.set("limit", String(PAGE_SIZE));

  if (!params.has("sort")) {
    params.set("sort", "relevance");
  }

  const { data } = await apiClient.get<ClubsResponse>(
    `/api/clubs?${params.toString()}`,
    { signal }
  );

  return data;
}

export default function ClubsPage() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <ClubsPageContent />
    </Suspense>
  );
}

function ClubsPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const queryString = searchParams.toString();

  const qParam =
    searchParams.get("q") ?? "";

  const sort =
    searchParams.get("sort") ?? "relevance";

  const campus =
    searchParams.get("campus") ?? "";

  const accepting =
    searchParams.get("accepting") ?? "";

  const activeTags =
    searchParams.getAll("tag");

  const [search, setSearch] =
    useState(qParam);

  const [clubs, setClubs] =
    useState<Club[]>([]);

  const [data, setData] =
    useState<ClubsResponse | null>(null);

  const [loading, setLoading] =
    useState(true);

  const [loadingMore, setLoadingMore] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  const [loadMoreError, setLoadMoreError] =
    useState<string | null>(null);

  const [searchPending, setSearchPending] =
    useState(false);

  const loadMoreRef =
    useRef<HTMLDivElement | null>(null);

  const loadMoreAbortRef =
    useRef<AbortController | null>(null);

  const baseQueryString = useMemo(() => {
    const params =
      new URLSearchParams(queryString);

    params.delete("page");
    params.delete("limit");

    if (!params.has("sort")) {
      params.set("sort", "relevance");
    }

    return params.toString();
  }, [queryString]);

  useEffect(() => {
    setSearch(qParam);
  }, [qParam]);

  // Loads the first page whenever search/filter state changes.
  useEffect(() => {
    const controller =
      new AbortController();

    loadMoreAbortRef.current?.abort();

    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setLoadMoreError(null);
    setClubs([]);
    setData(null);

    async function loadFirstPage() {
      try {
        const result =
          await fetchClubPage(
            baseQueryString,
            1,
            controller.signal
          );

        setClubs(result.clubs);
        setData(result);
      } catch (err) {
        if (
          err instanceof DOMException &&
          err.name === "AbortError"
        ) {
          return;
        }

        console.error(err);

        setError(
          "Unable to load clubs right now."
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadFirstPage();

    return () => {
      controller.abort();
    };
  }, [baseQueryString]);

  // Debounces text search while keeping filters instantaneous.
  useEffect(() => {
    const value = search.trim();

    if (value === qParam) {
      setSearchPending(false);
      return;
    }

    setSearchPending(true);

    const timeout =
      window.setTimeout(() => {
        const params =
          new URLSearchParams(queryString);

        if (value) {
          params.set("q", value);
        } else {
          params.delete("q");
        }

        params.delete("page");
        params.delete("limit");

        setSearchPending(false);

        const query =
          params.toString();

        router.replace(
          query
            ? `/clubs?${query}`
            : "/clubs",
          {
            scroll: false,
          }
        );
      }, SEARCH_DEBOUNCE);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [
    search,
    qParam,
    queryString,
    router,
  ]);

  function updateURL(
    changes: Record<
      string,
      string | null
    >,
    tags?: string[]
  ) {
    const params =
      new URLSearchParams(
        searchParams.toString()
      );

    for (
      const [key, value]
      of Object.entries(changes)
    ) {
      if (
        value === null ||
        value === ""
      ) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    }

    if (tags !== undefined) {
      params.delete("tag");

      for (const tag of tags) {
        params.append("tag", tag);
      }
    }

    params.delete("page");
    params.delete("limit");

    const query =
      params.toString();

    router.push(
      query
        ? `/clubs?${query}`
        : "/clubs",
      {
        scroll: false,
      }
    );
  }

  function handleSearch(
    event: FormEvent
  ) {
    event.preventDefault();

    setSearchPending(false);

    updateURL({
      q: search.trim() || null,
    });
  }

  function toggleTag(tag: string) {
    const nextTags =
      activeTags.includes(tag)
        ? activeTags.filter(
            (item) => item !== tag
          )
        : [
            ...activeTags,
            tag,
          ];

    updateURL({}, nextTags);
  }

  function toggleAccepting() {
    updateURL({
      accepting:
        accepting === "true"
          ? null
          : "true",
    });
  }

  function clearFilters() {
    const params =
      new URLSearchParams();

    if (qParam) {
      params.set("q", qParam);
    }

    if (sort !== "relevance") {
      params.set("sort", sort);
    }

    const query =
      params.toString();

    router.push(
      query
        ? `/clubs?${query}`
        : "/clubs",
      {
        scroll: false,
      }
    );
  }

  const loadNextPage =
    useCallback(async () => {
      if (
        !data ||
        loading ||
        loadingMore ||
        data.page >= data.total_pages
      ) {
        return;
      }

      loadMoreAbortRef.current?.abort();

      const controller =
        new AbortController();

      loadMoreAbortRef.current =
        controller;

      setLoadingMore(true);
      setLoadMoreError(null);

      try {
        const result =
          await fetchClubPage(
            baseQueryString,
            data.page + 1,
            controller.signal
          );

        setClubs((current) => {
          const existingIDs =
            new Set(
              current.map(
                (club) => club.id
              )
            );

          const newClubs =
            result.clubs.filter(
              (club) =>
                !existingIDs.has(club.id)
            );

          return [
            ...current,
            ...newClubs,
          ];
        });

        setData(result);
      } catch (err) {
        if (
          err instanceof DOMException &&
          err.name === "AbortError"
        ) {
          return;
        }

        console.error(err);

        setLoadMoreError(
          "Couldn't load more clubs."
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoadingMore(false);
        }
      }
    }, [
      baseQueryString,
      data,
      loading,
      loadingMore,
    ]);

  // Starts fetching the next page before the user reaches the bottom.
  useEffect(() => {
    const target =
      loadMoreRef.current;

    if (
      !target ||
      !data ||
      loading ||
      loadingMore ||
      loadMoreError ||
      data.page >= data.total_pages
    ) {
      return;
    }

    const observer =
      new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            loadNextPage();
          }
        },
        {
          rootMargin:
            "500px 0px",
        }
      );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [
    data,
    loading,
    loadingMore,
    loadMoreError,
    loadNextPage,
  ]);

  const suggestedTags =
    useMemo(() => {
      const tags =
        new Set<string>(activeTags);

      for (const club of clubs) {
        for (
          const tag
          of club.tags ?? []
        ) {
          if (tags.size >= 8) {
            break;
          }

          tags.add(tag);
        }

        if (tags.size >= 8) {
          break;
        }
      }

      return Array.from(tags);
    }, [
      clubs,
      activeTags.join("|"),
    ]);

  const hasFilters =
    activeTags.length > 0 ||
    accepting !== "" ||
    campus !== "";

  return (
    <main className="flex flex-1 flex-col">
      <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 md:py-10 lg:px-8">

        {/* Header */}

        <header className="mb-6">
  <div
    className="
      rounded-[32px]
      border
      bg-background/80
      px-6 py-8
      shadow-sm
      sm:px-8 sm:py-10
    "
  >
    <Badge
      variant="secondary"
      className="
        mb-4
        rounded-full
        px-4 py-1
        text-sm font-medium
      "
    >
      Club Directory
    </Badge>

    <div className="max-w-3xl">
      <h1
        className="
          text-4xl
          font-bold
          tracking-tight
          sm:text-5xl
        "
      >
        Find Your People at{" "}
        <span className="text-[#9D2235]">
          Temple
        </span>
      </h1>

      <p
        className="
          mt-3
          max-w-xl
          text-base
          text-muted-foreground
          sm:text-lg
        "
      >
        Discover organizations that fit
        your interests.
      </p>
    </div>

    {!loading && data && (
      <div className="mt-6 flex flex-wrap gap-2">
        <Badge
          variant="outline"
          className="
            rounded-full
            px-3 py-1
            font-normal
          "
        >
          {data.total.toLocaleString()} clubs
        </Badge>

        {qParam && (
          <Badge
            variant="outline"
            className="
              rounded-full
              px-3 py-1
              font-normal
            "
          >
            Searching “{qParam}”
          </Badge>
        )}

        {activeTags.length > 0 && (
          <Badge
            variant="outline"
            className="
              rounded-full
              px-3 py-1
              font-normal
            "
          >
            {activeTags.length}{" "}
            {activeTags.length === 1
              ? "filter"
              : "filters"}{" "}
            active
          </Badge>
        )}
      </div>
    )}
  </div>
</header>

        {/* Search + Filters */}

<section className="border-b py-6">
  <div
    className="
      rounded-[28px]
      border
      bg-card/40
      p-4
      sm:p-5
    "
  >
    {/* Search */}

    <form onSubmit={handleSearch}>
      <div className="relative">
        <Search
          className="
            absolute left-5 top-1/2
            z-10 h-5 w-5
            -translate-y-1/2
            text-muted-foreground
          "
        />

        <Input
          value={search}
          onChange={(event) =>
            setSearch(event.target.value)
          }
          placeholder="Search clubs or interests..."
          className="
            h-14
            rounded-full
            border-0
            bg-muted/60
            pl-14
            pr-36
            text-base
            shadow-none
            ring-1
            ring-border/60
            transition
            focus-visible:ring-2
            focus-visible:ring-[#9D2235]/30
          "
        />

        {/* Search status */}

        <div
          className="
            pointer-events-none
            absolute right-5 top-1/2
            flex -translate-y-1/2
            items-center gap-2
            text-xs
            text-muted-foreground
          "
          aria-live="polite"
        >
          {searchPending ? (
            <>
              <span
                className="
                  h-2 w-2
                  animate-pulse
                  rounded-full
                  bg-[#9D2235]
                "
              />

              <span className="hidden sm:inline">
                searching…
              </span>
            </>
          ) : loading && qParam ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />

              <span className="hidden sm:inline">
                Updating…
              </span>
            </>
          ) : (
            <span className="hidden text-muted-foreground/60 sm:inline">
              
            </span>
          )}
        </div>
      </div>

      <button
        type="submit"
        className="sr-only"
      >
        Search
      </button>
    </form>

    {/* Filters + Sort */}

    <div
      className="
        mt-4 flex flex-col
        gap-3
        sm:flex-row
        sm:items-center
        sm:justify-between
      "
    >
      <div
        className="
          flex min-w-0
          items-center gap-2
          overflow-x-auto
          pb-1
          [scrollbar-width:none]
          [&::-webkit-scrollbar]:hidden
        "
      >
        <Button
          type="button"
          variant={
            accepting === "true"
              ? "default"
              : "outline"
          }
          size="sm"
          className="
            h-9 shrink-0
            rounded-full
            px-4
          "
          onClick={toggleAccepting}
        >
          <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
          Accepting
        </Button>

        {suggestedTags
          .slice(0, 5)
          .map((tag) => {
            const active =
              activeTags.includes(tag);

            return (
              <Button
                key={tag}
                type="button"
                variant={
                  active
                    ? "default"
                    : "outline"
                }
                size="sm"
                className="
                  h-9 shrink-0
                  rounded-full
                  px-4
                "
                onClick={() =>
                  toggleTag(tag)
                }
              >
                {tag}
              </Button>
            );
          })}

        {campus && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="
              h-9 shrink-0
              rounded-full
              px-4
            "
            onClick={() =>
              updateURL({
                campus: null,
              })
            }
          >
            <MapPin className="mr-1.5 h-3.5 w-3.5" />
            {campus}
          </Button>
        )}

        {hasFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="
              h-9 shrink-0
              rounded-full
              px-3
              text-muted-foreground
            "
            onClick={clearFilters}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Sort */}

      <select
        value={sort}
        onChange={(event) =>
          updateURL({
            sort: event.target.value,
          })
        }
        aria-label="Sort clubs"
        className="
          h-10 shrink-0
          rounded-full
          border
          bg-background
          px-4
          text-sm
          outline-none
          transition-colors
          focus:border-[#9D2235]/50
          sm:w-[165px]
        "
      >
        <option value="relevance">
          Relevance
        </option>

        <option value="members">
          Most members
        </option>

        <option value="rating">
          Top rated
        </option>

        <option value="name">
          A–Z
        </option>
      </select>
    </div>
  </div>
</section>

        {/* Results */}

        <section
          id="club-results"
          className="pt-7"
        >
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold">
                {qParam
                  ? `Results for “${qParam}”`
                  : "All Clubs"}
              </h2>

              {!loading &&
                !error &&
                data && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {data.total.toLocaleString()}{" "}
                    {data.total === 1
                      ? "club"
                      : "clubs"}{" "}
                    found
                  </p>
                )}
            </div>

            {!loading &&
              !error &&
              data &&
              data.total > 0 && (
                <p className="text-sm text-muted-foreground">
                  Showing{" "}
                  {clubs.length.toLocaleString()}{" "}
                  of{" "}
                  {data.total.toLocaleString()}
                </p>
              )}
          </div>

          {loading ? (
            <ClubSkeletons />
          ) : error ? (
            <ErrorState
              message={error}
            />
          ) : clubs.length === 0 ? (
            <EmptyResults
              q={qParam}
              onClear={
                hasFilters
                  ? clearFilters
                  : undefined
              }
            />
          ) : (
            <>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {clubs.map(
                  (club) => (
                    <ClubCard
                      key={club.id}
                      club={club}
                    />
                  )
                )}
              </div>

              {/* Infinite scroll sentinel */}

              <div
                ref={loadMoreRef}
                className="
                  mt-8 flex min-h-20
                  items-center
                  justify-center
                "
              >
                {loadingMore ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />

                    Loading more clubs…
                  </div>
                ) : loadMoreError ? (
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-sm text-muted-foreground">
                      {loadMoreError}
                    </p>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={
                        loadNextPage
                      }
                    >
                      Try again
                    </Button>
                  </div>
                ) : data &&
                  data.page >=
                    data.total_pages ? (
                  <p className="text-sm text-muted-foreground">
                    You&apos;ve reached the
                    end ·{" "}
                    {data.total.toLocaleString()}{" "}
                    {data.total === 1
                      ? "club"
                      : "clubs"}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground/60">
                    Keep scrolling to see
                    more
                  </p>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
}

function ClubCard({
  club,
}: {
  club: Club;
}) {
  return (
    <Card
      className="
        group flex h-full flex-col
        overflow-hidden rounded-[26px]
        border bg-background
        shadow-sm
        transition-all duration-200
        hover:-translate-y-0.5
        hover:border-[#9D2235]/30
        hover:shadow-md
      "
    >
      <CardHeader className="pb-3">
        <div className="flex items-start gap-4">
          <div
            className="
              flex h-14 w-14 shrink-0
              items-center justify-center
              overflow-hidden rounded-2xl
              border bg-muted
            "
          >
            {club.logo_url ? (
              <img
                src={club.logo_url}
                alt={`${club.name} logo`}
                width={56}
                height={56}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-xl font-bold text-[#9D2235]">
                {club.name
                  .charAt(0)
                  .toUpperCase()}
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="line-clamp-2 text-lg font-semibold leading-snug">
              {club.name}
            </h3>

            {club.campus && (
              <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />

                <span className="truncate">
                  {club.campus}
                </span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col pt-0">
        {club.tags?.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1.5">
            {club.tags
              .slice(0, 4)
              .map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="
                    rounded-full
                    px-2.5 py-1
                    font-normal
                  "
                >
                  {tag}
                </Badge>
              ))}

            {club.tags.length > 4 && (
              <Badge
                variant="secondary"
                className="
                  rounded-full
                  px-2.5 py-1
                  font-normal
                  text-muted-foreground
                "
              >
                +{club.tags.length - 4}
              </Badge>
            )}
          </div>
        )}

        <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
          {club.description ||
            "Explore this organization and learn more about what they do at Temple."}
        </p>
      </CardContent>

      {/* Stats footer */}

      <div
        className="
          mt-auto
          border-t
          px-6 py-2
          text-xs
          text-muted-foreground
        "
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />

            <span>
              {club.member_count != null
                ? club.member_count.toLocaleString()
                : "—"}
            </span>
          </div>

          {club.membership_type && (
            <>
              <span className="text-border">
                •
              </span>

              <span>
                {club.membership_type}
              </span>
            </>
          )}

          <span className="text-border">
            •
          </span>

          <div
            className={`
              flex items-center gap-1.5
              ${
                (club.accepting_members = true)
                  ? "text-[#9D2235]"
                  : "text-muted-foreground"
              }
            `}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />

            <span>
              {club.accepting_members
                ? "Recruiting now"
                : "Not accepting"}
            </span>
          </div>

          {club.star_rating != null && (
            <>
              <span className="text-border">
                •
              </span>

              <div className="flex items-center gap-1">
                <Star className="h-3.5 w-3.5" />
                <span>{club.star_rating}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Large CTA */}

      <CardFooter className="flex flex-row gap-2">
        <Button
        variant={"outline"}
          className="
            h-11
            rounded-full
            text-sm font-medium
          "
        >
          <Bookmark className="h-4 w-4" />
        </Button>
        <Button
          className="
            h-11 w-full flex-1
            rounded-full
            text-sm font-medium
          "
        >
          <Link
            href={`/clubs/${club.slug}`}
            className="flex items-center justify-center"
          >
            View club

            <ArrowUpRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

function ClubSkeletons() {
  return (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({
        length: 6,
      }).map((_, index) => (
        <Card
          key={index}
          className="overflow-hidden"
        >
          <Skeleton className="h-1 w-full rounded-none" />

          <CardHeader>
            <div className="flex gap-4">
              <Skeleton className="h-14 w-14 shrink-0 rounded-xl" />

              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-4/5" />

                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-3">
            <Skeleton className="h-3 w-full" />

            <Skeleton className="h-3 w-[92%]" />

            <Skeleton className="h-3 w-[65%]" />

            <div className="flex gap-2 pt-2">
              <Skeleton className="h-6 w-16 rounded-full" />

              <Skeleton className="h-6 w-20 rounded-full" />
            </div>

            <Skeleton className="mt-5 h-16 w-full rounded-lg" />
          </CardContent>

          <CardFooter className="justify-end border-t">
            <Skeleton className="h-9 w-24" />
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function EmptyResults({
  q,
  onClear,
}: {
  q: string;
  onClear?: () => void;
}) {
  return (
    <div
      className="
        flex min-h-[280px]
        flex-col items-center
        justify-center
        rounded-xl
        border border-dashed
        px-6 text-center
      "
    >
      <div
        className="
          mb-4 flex h-12 w-12
          items-center justify-center
          rounded-full bg-muted
        "
      >
        <Search className="h-5 w-5 text-muted-foreground" />
      </div>

      <h3 className="font-semibold">
        No clubs found
      </h3>

      <p className="mt-2 max-w-sm text-sm text-muted-foreground">
        {q
          ? `No clubs matched “${q}”. Try another search or remove a filter.`
          : "Try removing some filters to see more clubs."}
      </p>

      {onClear && (
        <Button
          variant="outline"
          className="mt-5"
          onClick={onClear}
        >
          Clear filters
        </Button>
      )}
    </div>
  );
}

function ErrorState({
  message,
}: {
  message: string;
}) {
  return (
    <div
      className="
        rounded-xl
        border border-destructive/20
        bg-destructive/5
        px-6 py-12
        text-center
      "
    >
      <p className="font-medium text-destructive">
        {message}
      </p>

      <p className="mt-1 text-sm text-muted-foreground">
        Check the API connection and
        try again.
      </p>
    </div>
  );
}

function PageSkeleton() {
  return (
    <main className="flex flex-1">
      <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Skeleton className="h-10 w-52" />

        <Skeleton className="mt-3 h-5 w-96 max-w-full" />

        <Skeleton className="mt-10 h-12 w-full rounded-xl" />

        <div className="mt-10">
          <ClubSkeletons />
        </div>
      </div>
    </main>
  );
}